/**
 * Use a Previous Video: the attorney's own videos (My Videos) and the ones
 * the whole firm shares (Firm Videos). Watch one, then use it; the library
 * keeps the file, so removing it from an intake never deletes it. A video a
 * template still uses can't be deleted.
 */
import { Check, MoreHorizontal, Play, Trash2, Video } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import type { WelcomeVideo } from "@/api/types/outgoingIntake";
import { WelcomeVideosAPI } from "@/api/welcomeVideosApi";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { WelcomeVideoPlayer } from "@/features/foreignNationalOutgoingIntakes/components/WelcomeVideoPlayer";

import { formatClock } from "./welcomeVideo";

type Scope = WelcomeVideo["scope"];

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which shelves to show; a template can only use the firm's. */
  scopes?: Scope[];
  onPick: (video: WelcomeVideo) => Promise<void> | void;
  /** The video in use now, marked. */
  currentId?: string | null;
};

const SECTION: Record<Scope, { label: string; empty: string }> = {
  user: {
    label: "My Videos",
    empty: "None yet. Check Save to My Videos when you record one."
  },
  firm: {
    label: "Firm Videos",
    empty: "None yet. A template's welcome video lands here."
  }
};

/** "0:42 · Used 3 times". */
export const videoMeta = (video: WelcomeVideo): string => {
  const length = video.duration_seconds
    ? (video.trim?.end ?? video.duration_seconds) - (video.trim?.start ?? 0)
    : null;
  const used = video.used_count ?? 0;
  return [
    length ? formatClock(length) : null,
    used ? `Used ${used} time${used === 1 ? "" : "s"}` : "Not used yet"
  ]
    .filter(Boolean)
    .join(" · ");
};

const deleteError = (e: unknown): string =>
  e instanceof Error ? e.message : "Couldn't delete the video";

export const LibraryDialog: FC<Props> = ({
  open,
  onOpenChange,
  scopes = ["user", "firm"],
  onPick,
  currentId
}) => {
  const [videos, setVideos] = useState<WelcomeVideo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [watching, setWatching] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const only = scopes.length === 1 ? scopes[0] : undefined;

  const load = useCallback(async () => {
    setError(null);
    try {
      setVideos(await WelcomeVideosAPI.list(only));
    } catch (e) {
      setVideos([]);
      setError(e instanceof Error ? e.message : "Couldn't load the videos");
    }
  }, [only]);

  useEffect(() => {
    if (!open) return;
    setVideos(null);
    setWatching(null);
    void load();
  }, [open, load]);

  const pick = async (video: WelcomeVideo) => {
    setBusyId(video.video_id);
    try {
      await onPick(video);
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't use the video");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (video: WelcomeVideo) => {
    setBusyId(video.video_id);
    try {
      await WelcomeVideosAPI.remove(video.video_id);
      setVideos((list) =>
        (list ?? []).filter((v) => v.video_id !== video.video_id)
      );
      toast.success(`Deleted “${video.title}”`);
    } catch (e) {
      toast.error(deleteError(e));
    } finally {
      setBusyId(null);
    }
  };

  const row = (video: WelcomeVideo) => {
    const current = video.video_id === currentId;
    const expanded = watching === video.video_id;
    return (
      <li key={video.video_id} className="flex flex-col gap-2 py-2">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setWatching(expanded ? null : video.video_id)}
            aria-label={`Watch ${video.title}`}
            className="relative flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-fill-2 text-fg-3"
          >
            <Play className="size-3.5" />
          </button>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-base text-fg">{video.title}</span>
            <span className="truncate text-sm text-fg-3">
              {videoMeta(video)}
            </span>
          </div>
          {current ? (
            <span className="flex items-center gap-1 text-sm text-fg-3">
              <Check className="size-3.5" /> In Use
            </span>
          ) : (
            <Button
              variant="secondary"
              size="xs"
              disabled={busyId !== null}
              onClick={() => void pick(video)}
              data-testid="library-use"
            >
              {busyId === video.video_id ? (
                <Spinner className="size-3" />
              ) : null}
              Use
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`More for ${video.title}`}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                variant="destructive"
                disabled={busyId !== null}
                onSelect={() => void remove(video)}
              >
                <Trash2 /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {expanded ? (
          <div className="aspect-video w-full overflow-hidden rounded-lg">
            <WelcomeVideoPlayer
              src={video.url}
              trim={video.trim}
              objectFit="contain"
            />
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="grid-cols-1 sm:max-w-lg"
        data-testid="welcome-video-library"
      >
        <DialogHeader>
          <DialogTitle>
            {only === "firm" ? "Use a Firm Video" : "Use a Previous Video"}
          </DialogTitle>
          <DialogDescription>
            The client sees it on the first screen, trimmed the way it was
            saved.
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {videos === null ? (
            <div className="flex justify-center py-8">
              <Spinner className="size-4" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-6 text-sm text-fg-3">
              {error}
              <Button variant="secondary" size="xs" onClick={() => void load()}>
                Try Again
              </Button>
            </div>
          ) : (
            scopes.map((scope) => {
              const list = videos.filter((v) => v.scope === scope);
              return (
                <section
                  key={scope}
                  className="flex flex-col"
                  data-testid={`library-${scope}`}
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium text-fg-2">
                    <Video className="size-3.5 text-fg-3" />
                    {SECTION[scope].label}
                  </span>
                  {list.length ? (
                    <ul className="flex flex-col divide-y divide-line-1">
                      {list.map(row)}
                    </ul>
                  ) : (
                    <p className="py-2 text-sm text-fg-3">
                      {SECTION[scope].empty}
                    </p>
                  )}
                </section>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
